import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useNutsAuth } from '../../context/NutsAuthContext';
import { forumApi } from './forumApi';
import { useForumCommunity } from './useForumCommunity';
import { COMMUNITY_BRANDS } from '../community/communityBrands';

const pickLang = (obj, lang) => {
  if (!obj) return '';
  const code = (lang || 'ca').split('-')[0];
  return obj[code] || obj.ca || obj.es || obj.en || '';
};

const LOGO_URL =
  '/api/static/brand/logo-h0.png';

const GOLF_EMOJIS = ['🏌️', '⛳', '🥜', '🏆', '🏔️', '☀️', '🔥', '👍', '❤️', '😂', '😅', '🙌'];

// Auto-detect URLs in text and render them as clickable links.
const URL_REGEX = /(https?:\/\/[^\s<>"]+)/g;
const Linkify = ({ text }) => {
  if (!text) return null;
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 1) {
          // Trim trailing punctuation that shouldn't be part of the URL
          const m = part.match(/^(.*?)([.,;:!?)\]]*)$/);
          const url = m ? m[1] : part;
          const trailing = m ? m[2] : '';
          return (
            <React.Fragment key={i}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4AF37] hover:text-[#E5C158] underline underline-offset-2 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37] break-all"
              >
                {url}
              </a>
              {trailing}
            </React.Fragment>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

// ---------- shared building blocks ----------
const PageShell = ({ children, testid }) => (
  <div className="relative min-h-screen w-full bg-[#0E4D2A] text-[#F2E8D5] overflow-x-hidden" data-testid={testid}>
    <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
      <span
        className="font-serif font-black text-[#083B1F]"
        style={{ fontSize: 'min(75vw, 75vh)', lineHeight: 1, opacity: 0.3 }}
        aria-hidden="true"
      >
        H0
      </span>
    </div>
    <div className="relative z-10">{children}</div>
  </div>
);

const TopBar = ({ backTo = '/nuts', backLabel = 'Nuts', right = null }) => {
  const { i18n } = useTranslation();
  const LANGS = [
    { code: 'ca', label: 'CA' },
    { code: 'es', label: 'ES' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
  ];
  return (
    <div className="flex items-center justify-between px-5 md:px-10 py-5">
      <Link
        to={backTo}
        data-testid="forum-back"
        className="group flex items-center gap-2 font-sans text-[10px] md:text-xs tracking-[0.32em] text-[#A8B4A6] hover:text-[#D4AF37] transition-colors uppercase"
      >
        <span className="inline-block transition-transform group-hover:-translate-x-1">←</span>
        {backLabel}
      </Link>
      <div className="flex items-center gap-5">
        {right}
        <div className="hidden md:flex gap-4 font-sans text-[11px] tracking-[0.25em]">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => i18n.changeLanguage(l.code)}
              className={`transition-colors duration-300 ${
                i18n.language === l.code ? 'text-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Hero = ({ eyebrow, title, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7 }}
    className="flex flex-col items-center text-center pt-4 pb-10"
  >
    <img src={LOGO_URL} alt="Hoyo Zero" className="w-14 sm:w-16 h-auto opacity-95" />
    <p className="mt-6 text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px]">{eyebrow}</p>
    <h1 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight max-w-3xl">{title}</h1>
    {subtitle && (
      <p className="mt-3 font-serif italic text-[#A8B4A6] text-base md:text-lg max-w-2xl">{subtitle}</p>
    )}
  </motion.div>
);

const formatRelative = (iso, lang = 'ca') => {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const diffMs = date.getTime() - Date.now();
    const diffMin = Math.round(diffMs / 60000);
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    const abs = Math.abs(diffMin);
    if (abs < 1) return rtf.format(0, 'minute');
    if (abs < 60) return rtf.format(diffMin, 'minute');
    if (abs < 60 * 24) return rtf.format(Math.round(diffMin / 60), 'hour');
    return rtf.format(Math.round(diffMin / (60 * 24)), 'day');
  } catch {
    return iso;
  }
};

// ---------- emoji picker ----------
const EmojiBar = ({ onPick }) => (
  <div className="flex flex-wrap gap-1 mb-2">
    {GOLF_EMOJIS.map((e) => (
      <button
        key={e}
        type="button"
        onClick={() => onPick(e)}
        className="w-8 h-8 rounded-md hover:bg-[#D4AF37]/15 transition-colors text-lg"
        data-testid={`forum-emoji-${e}`}
      >
        {e}
      </button>
    ))}
  </div>
);

// ====================================================================
// FORUM HOME — rooms list
// ====================================================================
export const ForumHome = () => {
  const { t, i18n } = useTranslation();
  const { getToken, loading, member } = useNutsAuth();
  const { community, basePath, dashboardPath } = useForumCommunity();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(true);

  const brandForum = COMMUNITY_BRANDS[community]?.forumBranding || COMMUNITY_BRANDS.nuts.forumBranding;
  const lang = i18n.language;

  useEffect(() => {
    if (loading) return;
    if (!member) {
      navigate(dashboardPath);
      return;
    }
    const token = getToken();
    forumApi.markSeen(token).catch(() => {});
    forumApi
      .listRooms(token)
      .then(setRooms)
      .catch(() => toast.error('No s\'han pogut carregar les sales'))
      .finally(() => setBusy(false));
  }, [loading, member, getToken, navigate, dashboardPath]);

  if (loading || busy) {
    return <PageShell><div className="py-32 text-center text-[#A8B4A6] tracking-widest">...</div></PageShell>;
  }

  return (
    <PageShell testid="forum-home">
      <TopBar backLabel={pickLang(brandForum.backLabel, lang)} backTo={dashboardPath} />
      <div className="max-w-3xl mx-auto px-5 md:px-10 pb-20">
        <Hero
          eyebrow={`${brandForum.emoji} ${pickLang(brandForum.eyebrow, lang)}`}
          title={pickLang(brandForum.title, lang)}
          subtitle={t('forum.subtitle')}
        />
        <div className="space-y-4">
          {rooms.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
            >
              <Link
                to={`${basePath}/r/${r.slug}`}
                data-testid={`forum-room-${r.slug}`}
                className="group block rounded-2xl border border-[#D4AF37]/15 hover:border-[#D4AF37]/45 bg-[#083B1F]/40 p-6 transition-all duration-500"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="text-2xl md:text-3xl flex-shrink-0">{r.icon || '💬'}</span>
                    <div className="min-w-0">
                      <h3 className="font-serif text-xl md:text-2xl tracking-tight">{r.name}</h3>
                      {r.description && (
                        <p className="mt-1 font-sans font-light text-[#A8B4A6] text-sm truncate">{r.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-serif text-[#D4AF37] text-2xl tabular-nums">{r.thread_count}</p>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#A8B4A6]">{t('forum.threads')}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
          {rooms.length === 0 && (
            <div className="py-16 text-center text-[#A8B4A6] font-serif italic">{t('forum.noRooms')}</div>
          )}
        </div>
      </div>
    </PageShell>
  );
};

// ====================================================================
// ROOM — threads list + create thread
// ====================================================================
const NewThreadForm = ({ roomSlug, onCreated }) => {
  const { t } = useTranslation();
  const { getToken, basePath } = useForumCommunity();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3 || !body.trim()) {
      toast.error(t('forum.shortContent'));
      return;
    }
    setSubmitting(true);
    try {
      const thread = await forumApi.createThread(getToken(), roomSlug, {
        title: title.trim(),
        body: body.trim(),
        image_url: imageUrl.trim() || null,
      });
      toast.success(t('forum.threadCreated'));
      setTitle('');
      setBody('');
      setImageUrl('');
      setOpen(false);
      onCreated?.(thread);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('forum.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="forum-new-thread-open"
        className="w-full border border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 rounded-2xl py-5 font-sans tracking-[0.28em] uppercase text-xs text-[#D4AF37] transition-all"
      >
        + {t('forum.newThread')}
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border border-[#D4AF37]/30 rounded-2xl p-5 bg-[#083B1F]/50 space-y-3"
      data-testid="forum-new-thread-form"
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('forum.threadTitlePlaceholder')}
        className="w-full bg-transparent border-b border-[#A8B4A6]/40 focus:border-[#D4AF37] outline-none py-2 font-serif text-xl text-[#F2E8D5] placeholder:text-[#A8B4A6]/50 transition-colors"
        data-testid="forum-new-thread-title"
        autoFocus
      />
      <EmojiBar onPick={(e) => setBody((b) => b + e)} />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={t('forum.threadBodyPlaceholder')}
        rows={4}
        className="w-full bg-transparent border border-[#A8B4A6]/20 focus:border-[#D4AF37]/50 outline-none p-3 rounded-lg font-sans text-sm text-[#F2E8D5] placeholder:text-[#A8B4A6]/50 transition-colors resize-y"
        data-testid="forum-new-thread-body"
      />
      <input
        type="url"
        value={imageUrl}
        onChange={(e) => setImageUrl(e.target.value)}
        placeholder={t('forum.imageUrlPlaceholder')}
        className="w-full bg-transparent border border-[#A8B4A6]/20 focus:border-[#D4AF37]/50 outline-none p-2 rounded-lg font-sans text-xs text-[#F2E8D5] placeholder:text-[#A8B4A6]/50"
        data-testid="forum-new-thread-image"
      />
      {imageUrl && <img src={imageUrl} alt="" className="max-h-40 rounded-lg" />}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#F2E8D5]"
        >
          {t('forum.cancel')}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="forum-new-thread-submit"
          className="bg-[#D4AF37] hover:bg-[#E5C158] disabled:opacity-50 text-[#0E4D2A] font-sans font-medium tracking-[0.18em] uppercase text-xs px-6 py-2.5 rounded-full transition-all duration-300"
        >
          {submitting ? t('forum.publishing') : t('forum.publish')}
        </button>
      </div>
    </form>
  );
};

export const ForumRoom = () => {
  const { t, i18n } = useTranslation();
  const { roomSlug } = useParams();
  const { getToken, loading, member } = useNutsAuth();
  const { community, basePath, dashboardPath } = useForumCommunity();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const brandForum = COMMUNITY_BRANDS[community]?.forumBranding || COMMUNITY_BRANDS.nuts.forumBranding;
  const lang = i18n.language;

  const load = useMemo(
    () => () =>
      forumApi
        .listThreads(getToken(), roomSlug)
        .then(setData)
        .catch(() => toast.error('Sala no trobada'))
        .finally(() => setBusy(false)),
    [getToken, roomSlug]
  );

  useEffect(() => {
    if (loading) return;
    if (!member) {
      navigate(dashboardPath);
      return;
    }
    load();
  }, [loading, member, navigate, load, dashboardPath]);

  if (loading || busy || !data) {
    return <PageShell><div className="py-32 text-center text-[#A8B4A6] tracking-widest">...</div></PageShell>;
  }
  const { room, threads } = data;

  return (
    <PageShell testid="forum-room">
      <TopBar backLabel={t('forum.allRooms')} backTo={basePath} />
      <div className="max-w-3xl mx-auto px-5 md:px-10 pb-20">
        <Hero
          eyebrow={`${room.icon || '💬'} ${room.name}`}
          title={room.description || pickLang(brandForum.roomSubtitle, lang)}
        />

        <NewThreadForm roomSlug={room.slug} onCreated={load} />

        <div className="mt-8 space-y-3">
          {threads.map((thr, i) => (
            <motion.div
              key={thr.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.03 }}
            >
              <Link
                to={`${basePath}/t/${thr.id}`}
                data-testid={`forum-thread-${thr.id}`}
                className="group block rounded-2xl border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 bg-[#083B1F]/30 p-5 transition-all duration-500"
              >
                <div className="flex items-start gap-4">
                  {thr.author_avatar && (
                    <img
                      src={thr.author_avatar}
                      alt={thr.author_nickname}
                      className="w-10 h-10 rounded-full border border-[#D4AF37]/30 bg-[#083B1F] flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {thr.pinned && <span className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">📌 PIN</span>}
                      {thr.locked && <span className="text-[10px] tracking-[0.25em] uppercase text-[#A8B4A6]">🔒</span>}
                      <h3 className="font-serif text-lg md:text-xl tracking-tight truncate">{thr.title}</h3>
                    </div>
                    <p className="mt-1 font-sans font-light text-[#A8B4A6] text-xs truncate">
                      {thr.author_nickname} · {formatRelative(thr.last_activity_at, i18n.language)} · {thr.comment_count} {t('forum.replies')}
                    </p>
                  </div>
                  <span className="text-[#D4AF37] group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </Link>
            </motion.div>
          ))}
          {threads.length === 0 && (
            <p className="py-12 text-center text-[#A8B4A6] font-serif italic">{t('forum.noThreads')}</p>
          )}
        </div>
      </div>
    </PageShell>
  );
};

// ====================================================================
// THREAD — comments + reply box
// ====================================================================
const CommentBox = ({ author, body, image_url, created_at, lang, deleted, edited, isOwn, onDelete }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="flex gap-3"
    data-testid="forum-comment"
  >
    {author.avatar && (
      <img
        src={author.avatar}
        alt={author.nickname}
        className="w-9 h-9 rounded-full border border-[#D4AF37]/25 bg-[#083B1F] flex-shrink-0 mt-0.5"
      />
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-serif text-[#F2E8D5] text-sm md:text-base">{author.nickname}</span>
        <span className="text-[10px] text-[#A8B4A6]/60">{formatRelative(created_at, lang)}{edited ? ' · editat' : ''}</span>
        {isOwn && !deleted && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto text-[10px] tracking-widest uppercase text-[#A8B4A6]/50 hover:text-red-400 transition-colors"
            data-testid="forum-comment-delete"
          >
            🗑
          </button>
        )}
      </div>
      {deleted ? (
        <p className="font-serif italic text-[#A8B4A6]/60 text-sm mt-1">— comentari esborrat —</p>
      ) : (
        <>
          <p className="mt-1 font-sans font-light text-[#F2E8D5]/95 whitespace-pre-wrap break-words text-sm md:text-base leading-relaxed">
            <Linkify text={body} />
          </p>
          {image_url && (
            <img src={image_url} alt="" className="mt-3 max-h-72 rounded-lg border border-[#D4AF37]/10" />
          )}
        </>
      )}
    </div>
  </motion.div>
);

export const ForumThreadView = () => {
  const { t, i18n } = useTranslation();
  const { threadId } = useParams();
  const { getToken, loading, member } = useNutsAuth();
  const { basePath, dashboardPath } = useForumCommunity();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [reply, setReply] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useMemo(
    () => () =>
      forumApi
        .getThread(getToken(), threadId)
        .then(setData)
        .catch(() => toast.error(t('forum.threadNotFound')))
        .finally(() => setBusy(false)),
    [getToken, threadId, t]
  );

  useEffect(() => {
    if (loading) return;
    if (!member) {
      navigate(dashboardPath);
      return;
    }
    load();
  }, [loading, member, navigate, load, dashboardPath]);

  const post = async (e) => {
    e.preventDefault();
    if (!reply.trim() && !imageUrl.trim()) return;
    setPosting(true);
    try {
      await forumApi.addComment(getToken(), threadId, {
        body: reply.trim(),
        image_url: imageUrl.trim() || null,
      });
      setReply('');
      setImageUrl('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('forum.genericError'));
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm(t('forum.confirmDelete'))) return;
    try {
      await forumApi.deleteComment(getToken(), cid);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('forum.genericError'));
    }
  };

  if (loading || busy || !data) {
    return <PageShell><div className="py-32 text-center text-[#A8B4A6] tracking-widest">...</div></PageShell>;
  }

  const { thread, comments } = data;

  return (
    <PageShell testid="forum-thread">
      <TopBar backLabel={t('forum.backToRoom')} backTo={basePath} />
      <div className="max-w-3xl mx-auto px-5 md:px-10 pb-32">
        {/* Thread head */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="border border-[#D4AF37]/15 bg-[#083B1F]/40 rounded-2xl p-6 mt-6"
        >
          <div className="flex items-center gap-2 flex-wrap">
            {thread.pinned && <span className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">📌 PIN</span>}
            {thread.locked && <span className="text-[10px] tracking-[0.25em] uppercase text-[#A8B4A6]">🔒</span>}
          </div>
          <h1 className="mt-1 font-serif text-2xl md:text-3xl tracking-tight">{thread.title}</h1>
          <div className="flex items-center gap-3 mt-4">
            {thread.author_avatar && (
              <img
                src={thread.author_avatar}
                alt={thread.author_nickname}
                className="w-10 h-10 rounded-full border border-[#D4AF37]/30 bg-[#083B1F] flex-shrink-0"
              />
            )}
            <div>
              <p className="font-serif text-[#F2E8D5] text-sm">{thread.author_nickname}</p>
              <p className="text-[10px] text-[#A8B4A6]/60">{formatRelative(thread.created_at, i18n.language)}</p>
            </div>
          </div>
          <p className="mt-5 font-sans font-light text-[#F2E8D5]/95 whitespace-pre-wrap break-words text-sm md:text-base leading-relaxed">
            <Linkify text={thread.body} />
          </p>
          {thread.image_url && (
            <img src={thread.image_url} alt="" className="mt-4 max-h-96 rounded-lg border border-[#D4AF37]/10" />
          )}
        </motion.div>

        {/* Comments */}
        <div className="mt-8 space-y-6">
          {comments.map((c) => (
            <CommentBox
              key={c.id}
              author={{ nickname: c.author_nickname, avatar: c.author_avatar }}
              body={c.body}
              image_url={c.image_url}
              created_at={c.created_at}
              lang={i18n.language}
              edited={c.edited}
              deleted={c.deleted}
              isOwn={c.author_id === member?.id}
              onDelete={() => deleteComment(c.id)}
            />
          ))}
        </div>

        {/* Reply box */}
        {!thread.locked && (
          <form
            onSubmit={post}
            className="mt-10 border border-[#D4AF37]/20 rounded-2xl p-4 bg-[#083B1F]/40"
            data-testid="forum-reply-form"
          >
            <EmojiBar onPick={(e) => setReply((r) => r + e)} />
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={t('forum.replyPlaceholder')}
              rows={3}
              className="w-full bg-transparent border border-[#A8B4A6]/20 focus:border-[#D4AF37]/50 outline-none p-3 rounded-lg font-sans text-sm text-[#F2E8D5] placeholder:text-[#A8B4A6]/50 transition-colors resize-y"
              data-testid="forum-reply-body"
            />
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder={t('forum.imageUrlPlaceholder')}
              className="w-full mt-2 bg-transparent border border-[#A8B4A6]/20 focus:border-[#D4AF37]/50 outline-none p-2 rounded-lg font-sans text-xs text-[#F2E8D5] placeholder:text-[#A8B4A6]/50"
              data-testid="forum-reply-image"
            />
            {imageUrl && <img src={imageUrl} alt="" className="mt-2 max-h-32 rounded-lg" />}
            <div className="flex justify-end mt-3">
              <button
                type="submit"
                disabled={posting || (!reply.trim() && !imageUrl.trim())}
                data-testid="forum-reply-submit"
                className="bg-[#D4AF37] hover:bg-[#E5C158] disabled:opacity-50 text-[#0E4D2A] font-sans font-medium tracking-[0.18em] uppercase text-xs px-6 py-2.5 rounded-full transition-all duration-300"
              >
                {posting ? t('forum.posting') : t('forum.reply')}
              </button>
            </div>
          </form>
        )}
      </div>
    </PageShell>
  );
};
